import { PitAssignments, PitBoard, PitColumn } from '../types';
import { authFetchStatus } from '../auth/api';

export const STORAGE_KEY = 'karting-pit-assignments';
export const STORAGE_VERSION = 2;

export const PIT_COLUMNS: PitColumn[] = ['red', 'green', 'blue'];

export const emptyBoard = (): PitBoard => ({ red: [], green: [], blue: [] });

interface StoredBoard {
  version: number;
  columns: PitBoard;
}

export function migrateV1(stored: PitAssignments): PitBoard {
  const board = emptyBoard();

  for (const column of PIT_COLUMNS) {
    const entries = Array.isArray(stored?.[column]) ? stored[column] : [];

    board[column] = entries.map((a, index) => {
      const occupantTeamId = a.isBlankKart
        ? a.isTemporarilyOccupied
          ? a.occupiedByTeam
          : undefined
        : a.teamId;

      return {
        slotId: a.isBlankKart ? a.teamId : `slot_${a.teamId}_${a.assignedAt}_${index}`,
        kartNumber: a.kartNumber,
        column,
        createdAt: a.assignedAt,
        rating: a.rating,
        averageTime: a.averageTime,
        origin: a.isManuallyAdded ? 'manual' : 'auto',
        occupant: occupantTeamId
          ? { teamId: occupantTeamId, driver: a.driver, since: a.assignedAt }
          : undefined,
      };
    });
  }
  return board;
}

export function parseStoredBoard(raw: string): PitBoard {
  const parsed = JSON.parse(raw) as StoredBoard | PitAssignments;

  if ((parsed as StoredBoard)?.version === STORAGE_VERSION) {
    const columns = (parsed as StoredBoard).columns;
    return {
      red: columns?.red ?? [],
      green: columns?.green ?? [],
      blue: columns?.blue ?? [],
    };
  }
  return migrateV1(parsed as PitAssignments);
}


/**
 * Where a pit board lives.
 *
 * The board began as a per-browser localStorage value, which meant two people on
 * the same pit wall saw two different boards. This is the seam that lets it live
 * on the server instead, while replay keeps using a local, per-event copy so
 * reviewing an old race can never write over a live one.
 */
/**
 * How a save ended. 'conflict' means somebody else wrote first; the store has
 * already handed their board to the subscriber, and the caller should put its
 * own unsaved changes back on top of it and save again.
 */
export type SaveResult = 'saved' | 'conflict' | 'failed';

export interface PitBoardStore {
  /** Stable identity; changing it reloads the board. */
  key: string;
  load(): Promise<PitBoard>;
  /** Resolves once this board, or a later one that supersedes it, is stored. */
  save(board: PitBoard): Promise<SaveResult>;
  /** Called when someone else changes the board. Returns an unsubscribe. */
  subscribe?(onRemote: (board: PitBoard) => void): () => void;
}

export function localPitBoardStore(storageKey: string): PitBoardStore {
  return {
    key: `local:${storageKey}`,
    async load(): Promise<PitBoard> {
      try {
        const stored = localStorage.getItem(storageKey);
        return stored ? parseStoredBoard(stored) : emptyBoard();
      } catch (error) {
        console.error('Error loading pit assignments from localStorage:', error);
        return emptyBoard();
      }
    },
    async save(board: PitBoard): Promise<SaveResult> {
      try {
        localStorage.setItem(
          storageKey,
          JSON.stringify({ version: STORAGE_VERSION, columns: board }),
        );
        return 'saved';
      } catch (error) {
        console.error('Error saving pit assignments to localStorage:', error);
        return 'failed';
      }
    },
  };
}

// ── Remote updates ──────────────────────────────────────────────────────────

export interface RemotePitBoard {
  board: PitBoard;
  version: number;
  /** The screen whose write this is; absent on boards from older backends. */
  clientId?: string | null;
}

const remoteListeners = new Set<(payload: RemotePitBoard) => void>();

/**
 * Called by the socket layer when the server pushes a board.
 *
 * A tiny bus rather than threading the socket through the pit hook: the hook
 * cares that the board changed, not where the message came from.
 */
export function publishRemotePitBoard(payload: RemotePitBoard): void {
  remoteListeners.forEach((listener) => listener(payload));
}

export interface ServerStoreOptions {
  /** Told when our write lost to somebody else's. */
  onConflict?: () => void;
  onError?: (message: string) => void;
}

function newClientId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * The board on the server, shared by every screen on the pit wall.
 *
 * Writes go out at once and one at a time. Edits made while one is in flight
 * collapse into the next write, which carries the version the previous one
 * came back with — two of our own writes can then never collide with each
 * other, which is what used to turn a second tap into a "conflict" against
 * the first and throw it away.
 *
 * Our own write comes back over the socket like anybody else's. It is tagged
 * with this screen's id and skipped: applying it would replace whatever has
 * been done here since it was sent.
 */
export function serverPitBoardStore(
  eventId: string,
  { onConflict, onError }: ServerStoreOptions = {},
): PitBoardStore {
  const clientId = newClientId();
  let version = 0;
  const subscribers = new Set<(board: PitBoard) => void>();

  /** Versions only go up: an ack can land after a newer broadcast. */
  const sawVersion = (v: unknown): void => {
    if (typeof v === 'number' && v > version) version = v;
  };
  const handToSubscribers = (board: PitBoard): void => {
    subscribers.forEach((fn) => fn(board));
  };

  const put = async (board: PitBoard): Promise<SaveResult> => {
    try {
      const res = await authFetchStatus(`/events/${eventId}/pit-board`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ board, version, clientId }),
      });

      if (res.status === 409) {
        // Somebody else moved a kart first. Their board becomes the base;
        // the hook puts our unsaved changes back on top and saves again.
        const detail = await res.json().catch(() => null);
        sawVersion(detail?.version);
        if (detail?.board) handToSubscribers(detail.board as PitBoard);
        onConflict?.();
        return 'conflict';
      }

      if (!res.ok) {
        onError?.(`Could not save the pit board (HTTP ${res.status})`);
        return 'failed';
      }

      const saved = await res.json();
      sawVersion(saved?.version);
      return 'saved';
    } catch {
      onError?.('Could not reach the server to save the pit board');
      return 'failed';
    }
  };

  // One write in flight; the newest board waits behind it with everyone who
  // asked for a save in the meantime, since the newest board contains theirs.
  let inFlight = false;
  let queued: { board: PitBoard; resolve: ((r: SaveResult) => void)[] } | null = null;

  const pump = async (): Promise<void> => {
    inFlight = true;
    while (queued) {
      const { board, resolve } = queued;
      queued = null;
      const result = await put(board);
      resolve.forEach((fn) => fn(result));
    }
    inFlight = false;
  };

  return {
    key: `server:${eventId}`,

    async load(): Promise<PitBoard> {
      const res = await authFetchStatus(`/events/${eventId}/pit-board`);
      if (!res.ok) throw new Error(`Could not load the pit board (HTTP ${res.status})`);
      const data = await res.json();
      sawVersion(data?.version);
      return (data?.board as PitBoard) ?? emptyBoard();
    },

    save(board: PitBoard): Promise<SaveResult> {
      return new Promise((resolve) => {
        if (queued) {
          queued.board = board;
          queued.resolve.push(resolve);
        } else {
          queued = { board, resolve: [resolve] };
        }
        if (!inFlight) void pump();
      });
    },

    subscribe(onRemote: (board: PitBoard) => void): () => void {
      subscribers.add(onRemote);
      const listener = (payload: RemotePitBoard): void => {
        if (payload.clientId === clientId) {
          // Our own write coming back round. Note the version; nothing to apply.
          sawVersion(payload.version);
          return;
        }
        // Already have this one, or newer: a broadcast that crossed our load
        // or an ack. Applying it would turn the clock back.
        if (payload.version <= version) return;
        sawVersion(payload.version);
        onRemote(payload.board);
      };
      remoteListeners.add(listener);
      return () => {
        remoteListeners.delete(listener);
        subscribers.delete(onRemote);
      };
    },
  };
}
